/**
 * Entrada da equipe. Só acessa quem o administrador cadastrou em Equipe.
 * Google ou e-mail e senha. A senha é opcional e cada pessoa cria a sua em "Sua conta".
 */
import { useEffect, useRef, useState } from 'react';
import { PRODUCT_NAME } from '@dotapps/core';
import { Icon, TextField, useToast } from '@dotapps/ui';
import { useAuth, type LoginResult } from '../../app/auth';
import { useTenant } from '../../app/tenant';
import { renderGoogleButton } from '../../lib/google';

export function LoginPage() {
  const { tenant, logo, mode } = useTenant();
  const { loginWithPassword, loginWithGoogle, enter } = useAuth();
  const toast = useToast();
  const gRef = useRef<HTMLDivElement>(null);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);

  const finish = (r: LoginResult) => {
    enter(r);
    toast(`Conectado como ${r.user.email}`);
  };

  useEffect(() => {
    if (!tenant.googleClientId || !gRef.current) return;
    renderGoogleButton(
      gRef.current,
      tenant.googleClientId,
      async credential => {
        try {
          finish(await loginWithGoogle(credential));
        } catch (err) {
          toast((err as Error).message, 'warn');
        }
      },
      mode === 'dark',
    ).catch(err => toast(err.message, 'warn'));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tenant.googleClientId, mode]);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true);
    try {
      finish(await loginWithPassword(email.trim(), password));
    } catch (err) {
      toast((err as Error).message, 'warn');
    } finally {
      setBusy(false);
    }
  }

  return (
    <section className="login">
      <div className="login-card">
        {logo ? (
          <img className="login-logo" src={logo} alt={tenant.name} />
        ) : (
          <span className="login-mk">{tenant.name.slice(0, 3).toUpperCase()}</span>
        )}
        <p className="eyebrow">{tenant.name} · acesso da equipe</p>
        <h1>Entre na plataforma</h1>
        <p className="lede">
          Use o e-mail cadastrado pelo escritório.{' '}
          {tenant.googleClientId
            ? 'Entre com o Google. Se preferir senha, crie a sua em "Sua conta".'
            : 'A senha é enviada pelo administrador.'}
        </p>
        {tenant.googleClientId && (
          <>
            <div className="google-slot" ref={gRef} />
            <div className="login-or">
              <span>ou com e-mail e senha</span>
            </div>
          </>
        )}
        <form className="login-form" onSubmit={submit}>
          <TextField
            label="E-mail"
            type="email"
            autoComplete="username"
            placeholder="nome@gmail.com"
            value={email}
            onValue={setEmail}
            required
          />
          <TextField
            label="Senha"
            type="password"
            autoComplete="current-password"
            placeholder="Sua senha"
            value={password}
            onValue={setPassword}
            required
          />
          <button className="btn primary" type="submit" disabled={busy}>
            <Icon name="arrow" />
            Entrar
          </button>
        </form>
        <p className="muted small">Esqueceu a senha? Peça a um administrador para gerar outra em Equipe.</p>
      </div>
      {/* assinatura do produto: o nome precisa bater com a tela de permissão do Google */}
      <p className="login-sign">{PRODUCT_NAME}</p>
    </section>
  );
}
